import moment from 'moment';

/* -------------------------------------------------------------------------- */
/*                          Delivery Transition Thresholds                     */
/* -------------------------------------------------------------------------- */

// When in-transit progress reaches this %, consider transitioning to delivery phase
const DELIVERY_DISTANCE_THRESHOLD = 80;

// When time remaining to delivery is less than this (in hours), consider transitioning to delivery phase
const DELIVERY_TIME_THRESHOLD_HOURS = 12;

export interface OrderProgressResult {
    Booked: number;
    BookedInProgress: boolean;
    Dispatched: number;
    DispatchedInProgress: boolean;
    InTransit: number;
    InTransitInProgress: boolean;
    Delivery: number;
    DeliveryInProgress: boolean;
    Completed: number;
    CompletedInProgress: boolean;
    isNearDelivery: boolean;
}

/* -------------------------------------------------------------------------- */
/*                               Helper Functions                             */
/* -------------------------------------------------------------------------- */

// Combine separate date + time into a UTC ISO string for comparison via moment.utc()
// Times are stored as location-local time in the given timezone — convert to UTC for consistent unix comparison
const combineDatetime = (date: string, time: string, tz: string): string | null => {
    if (!date || !time || !tz) return null;

    return moment.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm:ss', tz).toISOString();
};

// Calculate pickup → priority time (window: end time; appointment/open: start time)
const getPriorityPickup = (sch: any, timezone: any) => {
    const pickupTz = sch?.pickupTimezone || timezone;
    const windowType = sch?.currentPickupWindowType || sch?.initialPickupWindowType || 'open';
    const isWindow = windowType === 'ready_time' || windowType === 'delivery_time';

    if (isWindow && sch?.currentPickupEndDate && sch?.currentPickupEndTime) {
        return combineDatetime(sch.currentPickupEndDate, sch.currentPickupEndTime, pickupTz);
    }

    return combineDatetime(sch?.currentPickupStartDate, sch?.currentPickupStartTime, pickupTz);
};

// Calculate delivery → priority time (window: end time; appointment/open: start time)
const getPriorityDelivery = (sch: any, timezone: any) => {
    const deliveryTz = sch?.deliveryTimezone || timezone;
    const windowType = sch?.currentDeliveryWindowType || sch?.initialDeliveryWindowType || 'open';
    const isWindow = windowType === 'ready_time' || windowType === 'delivery_time';

    if (isWindow && sch?.currentDeliveryEndDate && sch?.currentDeliveryEndTime) {
        return combineDatetime(sch.currentDeliveryEndDate, sch.currentDeliveryEndTime, deliveryTz);
    }

    return combineDatetime(sch?.currentDeliveryStartDate, sch?.currentDeliveryStartTime, deliveryTz);
};

// Parse any date input to a valid moment object
const parseDate = (date: any) => {
    if (!date) return null;

    return moment.utc(date);
};

// Clamp progress to avoid 100% auto-advancing
const clamp = (value: number) => Math.min(99, Math.max(0, Math.round(value)));

// % progress between two dates
const calculateProgress = (start: any, end: any) => {
    if (!start || !end) return 0;

    const s = start.unix();
    const e = end.unix();
    const now = moment.utc().unix();

    if (e <= s) return 0;

    const pct = ((now - s) / (e - s)) * 100;

    return clamp(pct);
};

/* -------------------------------------------------------------------------- */
/*                       Convert internal → final structure                    */
/* -------------------------------------------------------------------------- */

const convert = (s: any) =>
    ({
        Booked: s.Booked.pct,
        BookedInProgress: s.Booked.inProgress,
        Dispatched: s.Dispatched.pct,
        DispatchedInProgress: s.Dispatched.inProgress,
        InTransit: s.InTransit.pct,
        InTransitInProgress: s.InTransit.inProgress,
        Delivery: s.Delivery.pct,
        DeliveryInProgress: s.Delivery.inProgress,
        Completed: s.Completed.pct,
        CompletedInProgress: s.Completed.inProgress,
        isNearDelivery: s.isNearDelivery ?? false,
    } as OrderProgressResult);

/* -------------------------------------------------------------------------- */
/*                             Main Progress Logic                             */
/* -------------------------------------------------------------------------- */

export const calculateOrderProgress = (activeOrder: any, appearance: any, locationProgressPercentage?: number | null): OrderProgressResult => {
    const statuses: any = {
        Booked: { pct: 0, inProgress: false },
        Dispatched: { pct: 0, inProgress: false },
        InTransit: { pct: 0, inProgress: false },
        Delivery: { pct: 0, inProgress: false },
        Completed: { pct: 0, inProgress: false },
        isNearDelivery: false,
    };

    // If missing order status → default to Booked
    if (!activeOrder?.orderStatus) {
        statuses.Booked.inProgress = true;

        return convert(statuses);
    }

    const sch = activeOrder.pickupDeliverySchedule;
    const status = activeOrder.orderStatus.toLowerCase().replace(/_/g, '-');

    const timezone = appearance?.timezone;

    const priorityPickup = sch ? getPriorityPickup(sch, timezone) : null;
    const priorityDelivery = sch ? getPriorityDelivery(sch, timezone) : null;

    /* ---------------------------------- Mark earlier statuses as 100% ---------------------------------- */
    const markCompleteBefore = (names: string[]) => {
        names.forEach((name) => {
            statuses[name].pct = 100;
            statuses[name].inProgress = false;
        });
    };

    /* ---------------------------------- Handle each status ---------------------------------- */

    switch (status) {
        case 'booked': {
            const created = parseDate(activeOrder.createdAt);
            const pickup = parseDate(priorityPickup);

            statuses.Booked.pct = calculateProgress(created, pickup);
            statuses.Booked.inProgress = true;
            break;
        }

        case 'dispatched': {
            markCompleteBefore(['Booked']);

            const created = parseDate(activeOrder.createdAt);
            const pickup = parseDate(priorityPickup);

            statuses.Dispatched.pct = calculateProgress(created, pickup);
            statuses.Dispatched.inProgress = true;
            break;
        }

        case 'in-transit': {
            markCompleteBefore(['Booked', 'Dispatched']);

            // Calculate in-transit progress
            let transitProgress: number;

            if (locationProgressPercentage != null && locationProgressPercentage > 0) {
                transitProgress = clamp(locationProgressPercentage);
            } else {
                // API field is actualPickupDatetime (lowercase t) — the old actualPickupDateTime
                // read never matched, so time-based transit progress was permanently 0.
                const actualPickup = parseDate(sch?.actualPickupDatetime ?? sch?.actualPickupDateTime);
                const delivery = parseDate(priorityDelivery);
                transitProgress = calculateProgress(actualPickup, delivery);
            }

            // Check if near delivery: in-transit progress >= threshold AND time to delivery <= threshold
            const deliveryTime = parseDate(priorityDelivery);
            const hoursUntilDelivery = deliveryTime ? deliveryTime.diff(moment.utc(), 'hours', true) : Infinity;

            // Here we can change condition if we want to make them || or && condition based on the business logic
            if (transitProgress >= DELIVERY_DISTANCE_THRESHOLD && hoursUntilDelivery <= DELIVERY_TIME_THRESHOLD_HOURS) {
                // Complete in-transit and start delivery progress
                statuses.InTransit.pct = 100;
                statuses.InTransit.inProgress = false;
                statuses.isNearDelivery = true;

                // Calculate delivery progress based on remaining time within the threshold window
                const deliveryPct = hoursUntilDelivery <= 0 ? 99 : clamp(((DELIVERY_TIME_THRESHOLD_HOURS - hoursUntilDelivery) / DELIVERY_TIME_THRESHOLD_HOURS) * 100);
                statuses.Delivery.pct = deliveryPct;
                statuses.Delivery.inProgress = true;
            } else {
                statuses.InTransit.pct = transitProgress;
                statuses.InTransit.inProgress = true;
            }
            break;
        }

        case 'delivered': {
            markCompleteBefore(['Booked', 'Dispatched', 'InTransit']);

            statuses.Delivery.pct = 100;
            statuses.Delivery.inProgress = false;
            break;
        }

        /* ------ Post Delivery Statuses ------ */
        case 'dispute':
        // falls through
        case 'invoice-created':
        // falls through
        case 'invoice-sent':
        // falls through
        case 'invoice-paid':

        // falls through
        case 'claim': {
            markCompleteBefore(['Booked', 'Dispatched', 'InTransit', 'Delivery']);

            const map: any = {
                dispute: 20,
                'invoice-created': 40,
                'invoice-sent': 60,
                'invoice-paid': 80,
                claim: 90,
            };

            statuses.Completed.pct = map[status] ?? 0;
            statuses.Completed.inProgress = true;

            break;
        }

        case 'completed': {
            statuses.Booked.pct = 100;
            statuses.Booked.inProgress = false;
            statuses.Dispatched.pct = 100;
            statuses.Dispatched.inProgress = false;
            statuses.InTransit.pct = 100;
            statuses.InTransit.inProgress = false;
            statuses.Delivery.pct = 100;
            statuses.Delivery.inProgress = false;
            statuses.Completed.pct = 100;
            statuses.Completed.inProgress = false;
            break;
        }

        default:
            statuses.Booked.inProgress = true;
    }

    return convert(statuses);
};
